import React from "react";
//#region types
import { TranslationKeys } from "translations";
//#endregion
//#region components
import Icon from "../../icon";
import { Grid, CircularProgress, AutocompleteRenderGroupParams, AutocompleteRenderOptionState } from "@mui/material";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function useAutoCompleteCommonProps(
  disabled?: boolean,
  hasError?: boolean,
  optionSuffix?: string,
  valueProp?: string,
  labelProp?: string,
  rootClass?: string
) {
  return {
    classes: {
      root: clsx(cssExports.autoCompleteRoot, rootClass),
      popper: cssExports.autoCompletePopper,
      paper: cssExports.autoCompleteContainer,
      option: cssExports.autoCompleteOption,
      groupLabel: cssExports.autoCompleteGroupTitle,
      noOptions: cssExports.autoCompleteNoOptions,
      inputRoot: clsx(
        disabled || hasError ? cssExports.autoCompleteInputWithEndAdornment : "",
        cssExports.autoCompleteInputRoot
      ),
      endAdornment: cssExports.autoCompleteEndAdornment,
      clearIndicator: cssExports.autoCompleteClearIndicator,
      popperDisablePortal: cssExports.popperDisabledPortal,
      popupIndicator: cssExports.autoCompletePopUpIndicator,
    },
    loadingText: (
      <Grid className={cssExports.loadingItem}>
        <Grid container direction="row" flexWrap="nowrap">
          <Grid item>
            <CircularProgress size={20} className={cssExports.buttonProgressWhite} />
          </Grid>
          <Grid item className={cssExports.loadingText}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Loading)}
          </Grid>
        </Grid>
      </Grid>
    ),
    noOptionsText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NoResults),
    renderOption: (
      elements: React.HTMLAttributes<HTMLLIElement>,
      option: any,
      state: AutocompleteRenderOptionState
    ) => {
      const label = typeof option === "string" || typeof option === "number" ? option : option[labelProp ?? "label"];
      const id = typeof option === "string" || typeof option === "number" ? option : option[valueProp ?? "value"];
      const matchString =
        typeof state.inputValue === "string" || typeof state.inputValue === "number"
          ? state.inputValue
          : state.inputValue
            ? state.inputValue[labelProp ?? "label"]
            : "";
      const labelMatches = match(label, matchString, { insideWords: true, findAllOccurrences: true });
      const descriptionMatches = match(option.description ?? "", matchString, {
        insideWords: true,
        findAllOccurrences: true,
      });
      const lableParts = parse(label, labelMatches);
      const descriptionParts = parse(option.description ?? "", descriptionMatches);

      return (
        <li {...elements} key={id} data-testid={`autoCompleteOption-${label?.replaceAll(" ", "-")}`}>
          <Grid key={id} container>
            <Grid container wrap="nowrap">
              {option.icon ? (
                <span className={cssExports.icon}>
                  <Icon icon={option.icon} />
                </span>
              ) : (
                <></>
              )}
              <span className={clsx(cssExports.optionTextContainer)}>
                <span className={cssExports.optionText}>
                  {lableParts.map((part, index) => (
                    <span
                      key={index}
                      className={clsx(
                        part.highlight ? cssExports.highlightText : "",
                        option.description ? cssExports.withSubText : ""
                      )}
                    >
                      {part.text}
                    </span>
                  ))}
                  {optionSuffix ? (
                    <span className={clsx(option.description ? cssExports.withSubText : "")}>{` ${optionSuffix}`}</span>
                  ) : (
                    ""
                  )}
                </span>
                {!!option.description && (
                  <span className={cssExports.subText}>
                    {descriptionParts.map((part, index) => (
                      <span key={index} className={part.highlight ? cssExports.highlightText : ""}>
                        {part.text}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            </Grid>
          </Grid>
        </li>
      );
    },
    renderGroup: (option: AutocompleteRenderGroupParams) => {
      return (
        <li key={option.key}>
          {!UtilsHelper.isEmpty(option.group) ? (
            <Grid container direction="row" className={cssExports.autoCompleteGroupTitle}>
              <Grid item xs>
                {option.group}
              </Grid>
            </Grid>
          ) : (
            <></>
          )}
          <ul>{option.children}</ul>
        </li>
      );
    },
  };
}

export default useAutoCompleteCommonProps;
